import { FC } from 'react';

import { Button, StickyBarContent } from '@common/components';
import { useTranslations } from '@lang';

import Portal from '@common/components/Portal';

import { SpaSelectionWrapper } from './SelectExperienceStep.styles';

import { some } from '@utils/some';
import { useFormContext } from 'react-hook-form';
import { ButtonWrapper } from '../../components/ExperienceDetails/ExperienceDetails.styles';
import { ExperiencesGroup } from '../../components/ExperiencesGroup';
import { SpaTitleWrapper } from '../../components/ExperiencesGroup/ExperiencesGroup.styles';
import { haveAllExperiencesBeenSelected } from '../../helpers/haveAllExperiencesBeenSelected';
import { PackagesFormValues } from '../../types';
import { PageTitle, StyledDateRangeIndicator } from '../FormSteps.styles';
import { useHashNavigation } from '../hooks/useHashNavigation';
import { usePackageConfiguration, useWizardNavigation, useCurrentStep } from '../../contexts';

export const SelectExperienceStep: FC = () => {
  const { journeyParams, rate } = usePackageConfiguration();
  const { currentFormStep, stickyBarId, handleNextStep } = useWizardNavigation();
  const { packageTotalPrice, hash } = useCurrentStep();

  useHashNavigation({ hash });
  const { t } = useTranslations();
  const { watch } = useFormContext<PackagesFormValues, any>();
  const selectedExperiences = watch('selectedExperiences');
  const totalGuests = journeyParams.adults + journeyParams.children;

  const currentStep = currentFormStep;
  const includedExperiences = (currentStep.selectExperienceOptions || []).filter(
    (option) => option.selectionsRequired === 0
  );

  const selectableExperiencesByDay = (currentStep.selectExperienceOptionsByDayNumber || []).map(
    (day) => ({
      ...day,
      options: (day?.options || [])
        .filter((option) => option.selectionsRequired)
        .map((option) => ({ ...option, dayNumber: day.dayNumber })),
    })
  );

  const numberOfSelections = (selectedExperiences || [])?.reduce(
    (total, current) => (current.isIncluded ? current.count * totalGuests : current.count) + total,
    0
  );

  const isValid = haveAllExperiencesBeenSelected({
    selectableExperiencesByDay,
    selectedExperiences,
    totalGuests,
    numberOfSelections,
  });

  const groupedIncludedExperiences = some(includedExperiences)
    ? {
        ...includedExperiences[0],
        options: [...new Set(includedExperiences.flatMap((exp) => exp.options || []))],
      }
    : null;

  return (
    <>
      <SpaSelectionWrapper>
        <div>
          <div>
            <SpaTitleWrapper>
              <PageTitle>Spa</PageTitle>
            </SpaTitleWrapper>

            {selectableExperiencesByDay.map((day) => {
              if (some(day.options)) {
                return (
                  <>
                    {day.options.map((selectionGroup) => (
                      <ExperiencesGroup
                        key={selectionGroup.id}
                        selectionGroup={selectionGroup}
                        dayNumber={day.dayNumber}
                      />
                    ))}
                  </>
                );
              }
            })}

            {groupedIncludedExperiences && (
              <ExperiencesGroup
                key={groupedIncludedExperiences?.id}
                selectionGroup={groupedIncludedExperiences}
                allOptionsIncluded
                dayNumber={0}
              />
            )}
          </div>
        </div>
      </SpaSelectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={rate?.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={journeyParams.fromDate}
              toDate={journeyParams.toDate}
            />
          }
          displayedPrice={packageTotalPrice}
          button={
            <ButtonWrapper>
              <Button isPrimary onClick={handleNextStep} disabled={!isValid}>
                {t('spa.package.nextStep')}
              </Button>
            </ButtonWrapper>
          }
        />
      </Portal>
    </>
  );
};
